import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ComingSoon from '../../../../components/ComingSoon';
import { leagueScorers, mergeSquadIntoPlayers } from '../../../../service/leagueTable';
import {
  StyledStatsCard,
  StyledStatTable,
  StyledStatHead,
  StyledStatRow,
  StyledPlayer,
  StyledStatPos,
  StyledStatValue,
  StyledEmpty,
} from './styled';

const PLACEHOLDER_ROWS = Array.from({ length: 10 }, (_, index) => index + 1);

const StatHeaders = ({ withApp }) => (
  <StyledStatHead $withApp={withApp}>
    <span>#</span>
    <span>Player</span>
    <span>Pos</span>
    {withApp ? <span>APP</span> : null}
    <span>G</span>
    <span>A</span>
    <span>G+A</span>
  </StyledStatHead>
);

const StatCells = ({ row, withApp }) => (
  <>
    <StyledPlayer>
      {row.symbol ? <img src={row.symbol} alt="" /> : null}
      <strong>{row.name}</strong>
    </StyledPlayer>
    <StyledStatPos>{row.pos || '—'}</StyledStatPos>
    {withApp ? (
      <StyledStatValue>{row.app != null ? row.app : '—'}</StyledStatValue>
    ) : null}
    <StyledStatValue>{row.g}</StyledStatValue>
    <StyledStatValue>{row.a}</StyledStatValue>
    <StyledStatValue>{row.ga}</StyledStatValue>
  </>
);

const LeagueStats = ({
  players,
  squad,
  myTeam,
  leagueId,
  teams,
  isLoading,
  onSelectPlayer,
}) => {
  const teamIds = useMemo(
    () =>
      new Set(
        (teams || [])
          .map((team) => (team?._id != null ? String(team._id) : null))
          .filter(Boolean)
      ),
    [teams]
  );
  const merged = useMemo(
    () => mergeSquadIntoPlayers(players, squad, myTeam),
    [players, squad, myTeam]
  );
  const { available, rows } = useMemo(
    () => leagueScorers(merged, { leagueId, teamIds, limit: 10 }),
    [merged, leagueId, teamIds]
  );
  const scored = useMemo(() => {
    const crest = {};
    (teams || []).forEach((team) => {
      if (team?._id && team.symbol) crest[String(team._id)] = team.symbol;
    });
    return rows
      .filter((row) => row.ga > 0)
      .map((row) => ({
        ...row,
        symbol: row.symbol || crest[String(row.teamId)] || '',
      }));
  }, [rows, teams]);
  const withApp = scored.some((row) => row.app != null);
  const hasPlayers = Array.isArray(players) && players.length > 0;
  const waiting = isLoading && !hasPlayers && !available;

  return (
    <StyledStatsCard>
      {waiting ? (
        <StyledEmpty>Loading…</StyledEmpty>
      ) : !available ? (
        <ComingSoon>
          <StyledStatTable>
            <StatHeaders withApp={false} />
            {PLACEHOLDER_ROWS.map((place) => (
              <StyledStatRow key={place} type="button" tabIndex={-1}>
                <span>{place}</span>
                <StyledPlayer>
                  <strong>—</strong>
                </StyledPlayer>
                <StyledStatPos>—</StyledStatPos>
                <StyledStatValue>—</StyledStatValue>
                <StyledStatValue>—</StyledStatValue>
                <StyledStatValue>—</StyledStatValue>
              </StyledStatRow>
            ))}
          </StyledStatTable>
        </ComingSoon>
      ) : scored.length === 0 ? (
        <StyledEmpty>No goals yet this season</StyledEmpty>
      ) : (
        <StyledStatTable>
          <StatHeaders withApp={withApp} />
          {scored.map((row, index) => (
            <StyledStatRow
              key={row.id}
              type="button"
              $withApp={withApp}
              onClick={() => onSelectPlayer(row.id)}
            >
              <span>{index + 1}</span>
              <StatCells row={row} withApp={withApp} />
            </StyledStatRow>
          ))}
        </StyledStatTable>
      )}
    </StyledStatsCard>
  );
};

StatHeaders.propTypes = {
  withApp: PropTypes.bool,
};

StatHeaders.defaultProps = {
  withApp: false,
};

StatCells.propTypes = {
  row: PropTypes.object.isRequired,
  withApp: PropTypes.bool,
};

StatCells.defaultProps = {
  withApp: false,
};

LeagueStats.propTypes = {
  players: PropTypes.array,
  squad: PropTypes.array,
  myTeam: PropTypes.object,
  leagueId: PropTypes.string,
  teams: PropTypes.array,
  isLoading: PropTypes.bool,
  onSelectPlayer: PropTypes.func,
};

LeagueStats.defaultProps = {
  players: [],
  squad: [],
  myTeam: null,
  leagueId: null,
  teams: [],
  isLoading: false,
  onSelectPlayer: () => {},
};

export default LeagueStats;
